import { useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { partners } from '@/data/partners'

/** Auto-scroll speed in pixels per second */
const SPEED = 50

/** CSS mask that reveals only the center of the viewport with smooth fade */
const COLOR_MASK =
  'linear-gradient(to right, transparent 10%, black 35%, black 65%, transparent 90%)'

export function PartnersSection() {
  const { t } = useTranslation()
  const trackRef = useRef<HTMLDivElement>(null)
  const colorTrackRef = useRef<HTMLDivElement>(null)
  const setRef = useRef<HTMLDivElement>(null)
  const offsetRef = useRef(0)
  const dragDeltaRef = useRef(0)
  const isDraggingRef = useRef(false)
  const dragStartXRef = useRef(0)
  const dragBaseRef = useRef(0)
  const rafRef = useRef(0)
  const lastTimeRef = useRef(0)
  const setWidthRef = useRef(0)

  // Measure the exact pixel width of one logo set.
  // Adjust offset when width changes (e.g. images loading) to keep position.
  useEffect(() => {
    const measure = () => {
      if (!setRef.current) return
      const newW = setRef.current.offsetWidth
      const oldW = setWidthRef.current

      if (oldW > 0 && newW > 0 && newW !== oldW) {
        let vis = offsetRef.current % oldW
        if (vis > 0) vis -= oldW
        offsetRef.current = vis
      }

      setWidthRef.current = newW
    }

    measure()
    const ro = new ResizeObserver(measure)
    if (setRef.current) ro.observe(setRef.current)
    return () => ro.disconnect()
  }, [])

  // rAF loop — never pauses, drives both tracks
  useEffect(() => {
    const tick = (time: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = time
      const dt = Math.min(time - lastTimeRef.current, 100)
      lastTimeRef.current = time

      offsetRef.current -= (SPEED * dt) / 1000

      const w = setWidthRef.current
      if (w > 0) {
        const total = offsetRef.current + dragDeltaRef.current
        let wrapped = total % w
        if (wrapped > 0) wrapped -= w
        const tx = `translate3d(${wrapped}px,0,0)`
        if (trackRef.current) trackRef.current.style.transform = tx
        if (colorTrackRef.current) colorTrackRef.current.style.transform = tx
      }

      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  // ── Drag handlers ──
  const onDown = useCallback((e: React.PointerEvent) => {
    isDraggingRef.current = true
    dragStartXRef.current = e.clientX
    dragBaseRef.current = dragDeltaRef.current
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }, [])

  const onMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingRef.current) return
    dragDeltaRef.current = dragBaseRef.current + (e.clientX - dragStartXRef.current)
  }, [])

  const onUp = useCallback(() => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false
    offsetRef.current += dragDeltaRef.current
    dragDeltaRef.current = 0
  }, [])

  // ── Logo renderers ──
  const renderGrayscaleLogos = (keyPrefix: string) =>
    partners.map((partner, i) => (
      <div
        key={`${keyPrefix}-${partner.name}-${i}`}
        className="flex-shrink-0 flex items-center justify-center px-8 md:px-12"
      >
        <img
          src={partner.logo}
          alt={partner.name}
          className="h-10 md:h-12 w-auto grayscale opacity-60 pointer-events-none"
          draggable={false}
        />
      </div>
    ))

  const renderColorLogos = (keyPrefix: string) =>
    partners.map((partner, i) => (
      <div
        key={`${keyPrefix}-${partner.name}-${i}`}
        className="flex-shrink-0 flex items-center justify-center px-8 md:px-12"
      >
        <img
          src={partner.logo}
          alt=""
          className="h-10 md:h-12 w-auto pointer-events-none"
          draggable={false}
        />
      </div>
    ))

  return (
    <section className="section-padding bg-surface-light overflow-hidden">
      <div className="container-main">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
          {t('partners.title')}
        </h2>
      </div>

      <div className="relative">
        {/* ── Grayscale layer (base) ── */}
        <div
          ref={trackRef}
          className="flex cursor-grab active:cursor-grabbing select-none touch-none"
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
        >
          <div ref={setRef} className="flex flex-shrink-0">
            {renderGrayscaleLogos('a')}
          </div>
          <div className="flex flex-shrink-0">
            {renderGrayscaleLogos('b')}
          </div>
          <div className="flex flex-shrink-0">
            {renderGrayscaleLogos('c')}
          </div>
        </div>

        {/* ── Color layer (masked to center viewport) ── */}
        <div
          className="absolute inset-0 pointer-events-none overflow-hidden"
          style={{
            maskImage: COLOR_MASK,
            WebkitMaskImage: COLOR_MASK,
          }}
        >
          <div ref={colorTrackRef} className="flex">
            <div className="flex flex-shrink-0">
              {renderColorLogos('ca')}
            </div>
            <div className="flex flex-shrink-0">
              {renderColorLogos('cb')}
            </div>
            <div className="flex flex-shrink-0">
              {renderColorLogos('cc')}
            </div>
          </div>
        </div>

        {/* ── Fade edges ── */}
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-surface-light to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-surface-light to-transparent z-10 pointer-events-none" />
      </div>
    </section>
  )
}
